import React, { useState, useMemo, useEffect } from 'react';
import { Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import SearchBar from '../SearchBar/SearchBar';
import TipsTable from './TipsTable';
import { PageHeader } from '../People/EmployeeDirectory/EmployeeDirectory.styles';
import { getMostRecentPayout, getEmployeeNamesByEid } from './tipsHistoryService';

const Tips = ({ handleOpenDrawer }) => {
  const employees = useSelector((state) => state.employees.employees);
  const [searchTerm, setSearchTerm] = useState('');
  const [lastPaymentData, setLastPaymentData] = useState({});
  const [isLoadingPaymentData, setIsLoadingPaymentData] = useState(false);

  // Fetch last payment data for all employees
  useEffect(() => {
    const fetchLastPaymentData = async () => {
      setIsLoadingPaymentData(true);
      const activeEmployees = employees.filter((emp) => emp.isActive === true);
      const paymentDataMap = {};

      // Fetch last payment data for all active employees
      const payouts = await Promise.all(
        activeEmployees.map(async (employee) => {
          try {
            const lastPayout = await getMostRecentPayout(employee.id);
            return { employeeId: employee.id, payout: lastPayout };
          } catch (error) {
            console.error(`Error fetching last payment for ${employee.displayName}:`, error);
            return { employeeId: employee.id, payout: null };
          }
        })
      );

      // Collect unique paidByEid values for payouts that don't have paidByDisplayName
      // (for backward compatibility with older payout documents)
      const eidsNeedingFetch = new Set();
      payouts.forEach(({ payout }) => {
        if (payout?.paidByEid && !payout?.paidByDisplayName) {
          eidsNeedingFetch.add(payout.paidByEid);
        }
      });

      // Fetch employee names only for payouts missing paidByDisplayName
      let employeeNamesByEid = {};
      if (eidsNeedingFetch.size > 0) {
        employeeNamesByEid = await getEmployeeNamesByEid([...eidsNeedingFetch]);
      }

      // Build payment data map
      payouts.forEach(({ employeeId, payout }) => {
        if (payout) {
          // Prefer paidByDisplayName from the document, fall back to fetched name for older documents
          const paidByName = payout.paidByDisplayName || employeeNamesByEid[payout.paidByEid] || null;
          paymentDataMap[employeeId] = {
            date: payout.timestamp.seconds * 1000,
            paidByName: paidByName,
            paymentMethod: payout.paymentMethod || null,
          };
        }
      });

      setLastPaymentData(paymentDataMap);
      setIsLoadingPaymentData(false);
    };

    if (employees.length > 0) {
      fetchLastPaymentData();
    }
  }, [employees]);

  // Filter employees by search term (only active employees)
  const filteredEmployees = useMemo(() => {
    return employees
      .filter((employee) => {
        const matchesSearch = employee.displayName?.toLowerCase().includes(searchTerm.toLowerCase());
        const isActive = employee.isActive === true;
        return matchesSearch && isActive;
      })
      .sort((a, b) => {
        // Sort first by outstanding tip amount (descending), then by name (ascending)
        const tipsOwedA = typeof a.tipsOwed === 'number' ? a.tipsOwed : 0;
        const tipsOwedB = typeof b.tipsOwed === 'number' ? b.tipsOwed : 0;

        // First sort by tips owed (descending - highest first)
        if (tipsOwedA !== tipsOwedB) {
          return tipsOwedB - tipsOwedA;
        }

        // Then sort alphabetically by name
        return (a.displayName || '').localeCompare(b.displayName || '');
      });
  }, [employees, searchTerm]);

  return (
    <>
      <PageHeader>
        <Typography variant="h3">People</Typography>
      </PageHeader>

      <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />

      {/* Tips table */}
      <TipsTable
        employees={filteredEmployees}
        lastPaymentData={lastPaymentData}
        handleOpenDrawer={handleOpenDrawer}
        isLoadingPaymentData={isLoadingPaymentData}
      />
    </>
  );
};

Tips.propTypes = {
  handleOpenDrawer: PropTypes.func.isRequired,
};

export default Tips;
